/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Page as PageComponent } from '@/components/Page';
import { useNotifications } from '@/data/Content/Notifications';
import { getProductJsonLd } from '@/data/Content/ProductJsonLd';
import { getSettings, useSettings } from '@/data/Settings';
import { getUser } from '@/data/User';
import { SOLEMTI_COOKIE } from '@/data/config/COOKIES';
import { ServerSideRuntimeProvider } from '@/data/context/origin';
import { ElasticSearchErrorResponse, TransactionErrorResponse } from '@/data/types/Basic';
import { Cache } from '@/data/types/Cache';
import { getCache } from '@/data/utils/getCache';
import { processError } from '@/data/utils/processError';
import { getPageProps } from '@/utils/getPageProps';
import { Switch } from '@/utils/switch';
import { Box } from '@mui/material';
import type { GetServerSideProps, NextPage } from 'next';
import { useCallback } from 'react';
import { SWRConfig } from 'swr';
interface Props {
	fallback?: Record<string, unknown>;
	showMaintenanceHTML?: string;
	showMaintenance?: boolean;
	origin?: string;
	productJsonLd?: Record<string, unknown>;
}

const GlobalSWRConfig = {
	revalidateOnFocus: false, // disable focus re-validations
	revalidateIfStale: false, // disable auto re-validations
	errorRetryCount: 5, // Máximo 5 intentos de reintento
	errorRetryInterval: 5000, // Intervalo de 5 segundos entre intentos
};

const errorMessage = [
	'Unexpected end of JSON input',
	`Failed to execute 'json' on 'Response': Unexpected end of JSON input`,
	`Unexpected token '<', \"<html>\r\n<h\"... is not valid JSON`,
];

const Page: NextPage<Props> = ({
	fallback,
	showMaintenance,
	showMaintenanceHTML,
	origin,
	productJsonLd,
}) => {
	const { settings } = useSettings();
	const { notifyError } = useNotifications();
	const onError = useCallback(
		(error: any, _key: string) => {
			if (errorMessage.find((message) => message === error.error.message)) {
				return null;
			}

			notifyError(processError(error as TransactionErrorResponse | ElasticSearchErrorResponse));
		},
		[notifyError]
	);

	if (showMaintenance) {
		return showMaintenanceHTML ? (
			<Box dangerouslySetInnerHTML={{ __html: showMaintenanceHTML }} />
		) : null;
	}

	// the check for `settings.storeId` is needed when navigating from a statically generated page
	// to this server-side rendered page. Different from initial page load, in this scenario,
	// the settings are from pageProps async ajax call
	return settings.storeId ? (
		<SWRConfig value={{ fallback, onError, ...GlobalSWRConfig }}>
			<ServerSideRuntimeProvider value={{ origin, productJsonLd }}>
				<PageComponent />
			</ServerSideRuntimeProvider>
		</SWRConfig>
	) : null;
};

export default Page;

const fetchMaintenanceHTML = async () => {
	try {
		const url = Switch(process.env.ENVTYPE)
			.case('auth', () => 'https://dp-imagenes.s3.us-west-2.amazonaws.com/restricted-access.html')
			.defaultTo(() => 'https://dp-imagenes.s3.us-west-2.amazonaws.com/maintenance-dportenis.html');

		const response = await fetch(url);
		const html = await response.text();
		return html;
	} catch {
		return null;
	}
};

export const getServerSideProps: GetServerSideProps = async (context) => {
	const cache: Cache = getCache();

	const settings = await getSettings(cache, context);
	const { error } = settings; // any errors encountered resolving store settings (store-id, etc.)

	const user = error ? {} : await getUser(cache, context);
	const {
		res,
		req: { url = '' },
	} = context;

	// for metrics
	Object.assign(res, { hclData: { storeId: settings?.storeId } });

	// not a initial page request (url.startsWith('/_next')
	// e.g. `/_next/data/development/en-US/bedroom.json?path=bedroom`
	// see `docs/cookie-session.md`

	const {
		layout = null,
		props = { fallback: {} },
		...rest
	}: any = error
		? { notFound: true }
		: settings.csrSession || (url.startsWith('/_next') && user.sessionError)
		? {}
		: await getPageProps({ context, cache });

	const cookieSolemti = context.req.cookies[SOLEMTI_COOKIE];
	const showMaintenance = process.env.IN_MAINTENANCE === 'Y' && !cookieSolemti?.includes('solemti');

	const origin = `${context.req.headers['x-forwarded-proto'] || 'https'}://${
		context.req.headers['host']
	}`;

	const productJsonLd = await getProductJsonLd(cache, layout, origin, context);

	return {
		...rest,
		props: {
			...props,
			showMaintenance,
			showMaintenanceHTML: showMaintenance ? await fetchMaintenanceHTML() : null,
			settings,
			origin,
			productJsonLd,
		},
	};
};
